import * as yup from 'yup';
import Button from '@ui/Button';
import Typography from '@ui/Typography';
import React, { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import getSymbolFromCurrency from 'currency-symbol-map';
import { Controller, useForm } from 'react-hook-form';
import { fetchViaProxy } from '@/actions/proxy-fetch';
import Input from '../next-ui/Input/Input';
import Checkbox from '../next-ui/Checkbox';
import Tooltip from '@ui/Tooltip';
import { first, isEmpty, isNumber, trim } from 'lodash-es';
import { MdEdit, MdClose, MdCheck } from 'react-icons/md';
import PostImageSelector from './PostImageSelector/PostImageSelector';
import { authorizedServerFetch } from '@/actions/server.actions';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import { useUpdateFundraiserDonation } from '@/actions/feed.actions';
import { getUserCurrency } from '@/utils/user.helper';
import { sanitizeNonProfitAddress } from '@/utils/nonProfitAddress.helper';
import AnimatedContent from './AnimatedContent';
import { IoMdInformationCircleOutline } from 'react-icons/io';

const schemaPublic = yup.object().shape({
  firstName: yup
    .string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be at most 50 characters'),
  lastName: yup
    .string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be at most 50 characters'),
  email: yup
    .string()
    .required('Email is required')
    .email('Email must be a valid email id'),
  agreeTerms: yup
    .boolean()
    .oneOf([true], 'Please agree to terms')
    .required('Please agree to terms'),
  // amount: yup
  //   .number()
  //   .required()
  //   .min(1, 'Minimum amount is 1')
  //   .typeError('Minimum amount is 1'),
});

const schemaCommon = yup.object().shape({
  agreeTerms: yup
    .boolean()
    .oneOf([true], 'Please agree to terms')
    .required('Please agree to terms'),
  amount: yup
    .number()
    .required()
    .min(1, 'Minimum amount is 1')
    .typeError('Minimum amount is 1'),
});

const PendingDonationScreen = (props) => {
  const {
    user,
    post,
    amount,
    nonProfit,
    isLoggedIn,
    setShowSuccess,
    setLoggedOutUser,
    setTransactionState,
    handleFetchDonationIntent,
  } = props;

  const [noDonationUrlError, setNoDonationUrlError] = useState(false);
  const [matchedAmount, setMatchedAmount] = useState(null);
  const [showMatchAnimation, setShowMatchAnimation] = useState(false);

  const schema = isLoggedIn() ? schemaCommon : schemaPublic;

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      agreeTerms: false,
      amount: amount,
    },
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = form;

  const amountVal = watch('amount');

  const handleCompanyMatch = async () => {
    const { ok, data } = await fetchViaProxy(
      `/proxy/v1/users/me/donations/employer-matching?donationAmount=${amountVal}`,
    );
    if (ok && data?.data?.matchAmount) {
      setMatchedAmount(data.data.matchAmount);
      setShowMatchAnimation(true);
    }
  };

  useEffect(() => {
    if (isLoggedIn() && amountVal) {
      handleCompanyMatch();
    }
  }, [amountVal]);

  useEffect(() => {
    if (showMatchAnimation) {
      setTimeout(() => {
        setShowMatchAnimation(false);
      }, 50);
    }
  }, [showMatchAnimation]);

  const handleFormSubmit = async (formData) => {
    if (!nonProfit?.donation_url) {
      setNoDonationUrlError(true);
      return;
    }

    let payload = {
      nonProfitId: nonProfit.id,
      amount: formData.amount || 0,
      // currency: user?.location?.currency || 'USD',
      currency: isLoggedIn() ? getUserCurrency(user) : 'USD',
      postId: post?.id,
    };

    if (!isLoggedIn()) {
      payload = {
        ...payload,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
      };
      setLoggedOutUser({
        id: -1,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
      });
    }

    if (isLoggedIn()) {
      const { ok, data } = await fetchViaProxy(
        `/proxy/v2/protected/users/me/external/donations/intent`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
      );

      console.log('PendingDonationScreen', data);

      if (ok) {
        const intentId = data?.data?._id.toString();
        handleOpenExternalLink(nonProfit?.donation_url);
        setTransactionState('confirm');
        handleFetchDonationIntent(intentId, false);
      } else {
        // toast('Error in creating donation intent', { type: 'error' });
      }
    } else {
      /* empty */
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_HOST}/v1/public/donations/external/intent`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      );

      console.log('raiseStripeDonationIntentPublic res', res);

      if (res.ok) {
        const { data } = await res.json();
        console.log('PendingDonationScreen', data);
        const intentId = data?._id.toString();
        handleOpenExternalLink(nonProfit?.donation_url);
        // setTransactionState('confirm');
        // handleFetchDonationIntent(intentId, false);
        setTransactionState('pledged');
        setShowSuccess('user-external-donation');
      } else {
        // toast('Error in creating donation intent', { type: 'error' });
      }
    }
  };

  const handleOpenExternalLink = () => {
    let externalUrl = nonProfit?.donation_url;
    if (!/^https?:\/\//i.test(externalUrl)) {
      externalUrl = `https://${externalUrl}`;
    }
    window.open(externalUrl, '_blank');
  };

  return (
    <div className="flex flex-col">
      <div className="flex h-full flex-1 flex-col overflow-y-auto overflow-x-hidden">
        <div className="flex flex-1 flex-col justify-between gap-4">
          <div className="flex flex-col gap-4">
            <Typography variant="subtitle1" className="flex gap-4 py-1">
              You are leaving Neki and being re-directed to the donation page
              for {nonProfit.name}. After completing your donation on the
              external site, please save your proof of donation.
            </Typography>
          </div>

          <form
            className="flex flex-col justify-start gap-4 lg:flex-row lg:justify-between"
            onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="flex flex-col gap-4 py-1">
              {!isLoggedIn() && (
                <>
                  <div className="flex flex-col gap-4 lg:flex-row">
                    <Controller
                      name="firstName"
                      control={control}
                      render={({ field: { ref, ...rest } }) => (
                        <Input
                          label="First Name"
                          required
                          {...rest}
                          error={errors.firstName?.message}
                        />
                      )}
                    />
                    <Controller
                      name="lastName"
                      control={control}
                      render={({ field: { ref, ...rest } }) => (
                        <Input
                          label="Last Name"
                          required
                          {...rest}
                          error={errors.lastName?.message}
                        />
                      )}
                    />
                  </div>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field: { ref, ...rest } }) => (
                      <Input
                        label="Email Id"
                        required
                        {...rest}
                        error={errors.email?.message}
                      />
                    )}
                  />
                </>
              )}
              {isLoggedIn() && (
                <>
                  <Typography variant="body1" className="font-medium">
                    Donation Amount <span className="text-red-700">*</span>
                  </Typography>
                  <Controller
                    name="amount"
                    control={control}
                    render={({ field: { ref, ...rest } }) => (
                      <Input
                        // label="Donation Amount"
                        placeholder="Enter an amount"
                        required
                        {...rest}
                        adornment={
                          <Typography
                            variant="body1"
                            color="textSecondary"
                            className="!font-medium">
                            {/* {user?.location?.currencySymbol} */}
                            {isLoggedIn()
                              ? getSymbolFromCurrency(getUserCurrency(user))
                              : '$'}
                          </Typography>
                        }
                        error={errors.amount?.message}
                      />
                    )}
                  />
                </>
              )}
            </div>

            <div className="flex flex-col gap-4 self-end lg:gap-6">
              {isLoggedIn() && matchedAmount && (
                <div className="relative flex">
                  <Typography
                    variant="h5"
                    component="div"
                    // color="textSecondary"
                    className="flex items-center justify-center">
                    Company Donation Match
                    <Tooltip
                      className="w-[250px]"
                      content="Congratulations! You've successfully utilized your company's donation matching program. Your company will add to your donation, amplifying your impact!">
                      <IoMdInformationCircleOutline className="ml-2 h-5 w-5" />
                    </Tooltip>
                  </Typography>
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    className="ml-auto">
                    {getSymbolFromCurrency(getUserCurrency(user))}{' '}
                    {(matchedAmount || 0.0).toFixed(2)}
                  </Typography>
                  <div className="absolute right-0 top-0">
                    <AnimatedContent
                      duration={1500}
                      show={showMatchAnimation}
                      content={`Your company will contribute ${getSymbolFromCurrency(
                        getUserCurrency(user),
                      )} ${(matchedAmount || 0.0).toFixed(2)}`}
                    />
                  </div>
                </div>
              )}
              <Controller
                name="agreeTerms"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <div className="flex flex-col items-start gap-1">
                    <div className="flex items-center justify-start gap-4">
                      <Checkbox
                        {...rest}
                        className="!ml-0 border-text-disabled font-extrabold"
                        style={{ marginLeft: 0, paddingLeft: 0 }}
                      />
                      <Typography
                        variant="body2"
                        color="textDisabled"
                        className="font-bold">
                        I agree to the{' '}
                        <Typography
                          variant="body2"
                          component="a"
                          color="hyperlink"
                          target="_blank"
                          href="https://www.neki.io/terms-and-conditions"
                          className="text-primary hover:underline">
                          Terms of Service
                        </Typography>{' '}
                        and{' '}
                        <Typography
                          variant="body2"
                          component="a"
                          color="hyperlink"
                          target="_blank"
                          href="https://www.neki.io/privacy-policy"
                          className="text-primary hover:underline">
                          Privacy Policy
                        </Typography>
                      </Typography>
                    </div>
                    <Typography
                      variant="body2"
                      color="error"
                      className={!errors.agreeTerms && 'hidden'}>
                      Please agree to proceed
                    </Typography>
                  </div>
                )}
              />
              <Button
                type="submit"
                variant="filled"
                className="flex w-full items-center justify-center bg-primary">
                Proceed to External Donation Page
              </Button>
              <Typography
                variant="body2"
                color="error"
                className={!noDonationUrlError && 'hidden'}>
                The nonprofit has not yet set the donation url in the nonprofit
                profile. Please ask them to set it up there and comeback.
              </Typography>
            </div>
          </form>

          <Typography
            variant="caption"
            color="textSecondary"
            className="mt-4 pb-14 text-justify lg:pb-0">
            Please be aware that we are redirecting you to third-party websites
            for donation transactions. Neki is not responsible for the donation
            transaction or the content or practices of these external sites and
            does not process donations directly. Your transactions and
            confirmations of donations are managed independently on the
            third-party site, and you are subject to their terms and conditions.
            After completing your donation on the third-party site, it&apos;s
            important to confirm the transaction on Neki&apos;s platform. This
            confirmation may require you to provide proof of other forms of
            verification of the donation. We encourage you to review these
            points for a clear understanding of our process and your
            responsibilities.
          </Typography>
        </div>
      </div>
    </div>
  );
};

const ConfirmDonationScreen = (props) => {
  const {
    user,
    post,
    nonProfit,
    isLoggedIn,
    handleClose,
    donationIntent,
    setShowSuccess,
    setTransactionState,
  } = props;

  const { mutate: updateFundraiserDonation } = useUpdateFundraiserDonation();
  const isLessThanSmallScreen = useBreakpointHooks((bp) => bp.useSmaller('sm'));

  const [savedAmount, setSavedAmount] = useState(null);
  const [donationImage, setDonationImage] = useState([]);
  const [isAmountEditable, setIsAmountEditable] = useState(false);
  const [donationProofError, setDonationProofError] = useState(false);

  const address = sanitizeNonProfitAddress(nonProfit?.address, nonProfit);
  const addressString = [
    address.address,
    address.city,
    address.state,
    address.country,
    address.zipcode,
  ]
    .filter((item) => !isEmpty(trim(item)))
    .join(', ');

  const schema = yup.object().shape({
    amount: yup
      .number()
      .min(1, 'Minimum amount is 1')
      .typeError('Minimum amount is 1')
      .nullable()
      .notRequired(),
  });

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: {
      amount: donationIntent?.amount || 0,
    },
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    getValues,
  } = formMethods;

  const handleEditAmount = () => {
    setIsAmountEditable((prevIsAmountEditable) => !prevIsAmountEditable);
  };

  const handleSaveAmount = () => {
    const values = getValues();
    setSavedAmount(values.amount);
    handleEditAmount();
  };

  const handleClearAmount = () => {
    setValue('amount', donationIntent?.amount);
    handleEditAmount();
  };

  const handleUpdateDonationImage = (image) => {
    setDonationImage(image);
    setDonationProofError(false);
  };

  const handleUpdateFundraiserDonation = (amount) => {
    updateFundraiserDonation({
      postId: post?.id || donationIntent?.postId,
      amount: amount,
      donor: isLoggedIn()
        ? user
        : {
            id: -1,
            first_name: donationIntent.firstName,
            last_name: donationIntent.lastName,
            avatar: null,
          },
    });
  };

  const handleFormSubmit = async (data) => {
    const formData = new FormData();

    if (donationImage.length) {
      setDonationProofError(false);
      formData.append('donationProof', first(donationImage));
    } else {
      setDonationProofError(true);
      return;
    }

    const payload = {
      nonProfitId: nonProfit.id,
      intentId: donationIntent?._id,
      donorId: donationIntent?.donorId,
      donationId: donationIntent?.donationId,
      locationId: donationIntent?.locationId,
      currency: donationIntent?.currency,
      programId: donationIntent?.programId,
      matchId: donationIntent?.matchId,
      donationType: donationIntent?.donationType,
    };

    if (data.amount && isNumber(data.amount)) {
      payload.amount = data.amount;
    }

    formData.append('data', JSON.stringify(payload));

    if (isLoggedIn()) {
      let apiPath = `users/me/external/donations/confirm`;

      switch (donationIntent?.donationType) {
        case 'process-match':
          apiPath = `users/me/external/donation-match/confirm`;
          break;
        case 'adhoc-match':
          break;
        case 'process-gift-of-giving':
          break;
        default:
          break;
      }

      const { ok, data: res } = await authorizedServerFetch(apiPath, {
        method: 'POST',
        body: formData,
      });

      console.log('ConfirmDonationScreen', res);

      if (ok) {
        const processedAmount = res?.data?.totalDonated || data.amount;
        handleUpdateFundraiserDonation(processedAmount);
        setTransactionState('confirmed');
        setShowSuccess('user-external-donation');
      } else {
        // toast('Error confirming donation', { type: 'error' });
      }
    } else {
      /* empty */

      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_HOST}/v1/public/donations/external/confirm`,
        {
          method: 'POST',
          body: formData,
        },
      );

      console.log('ConfirmDonationScreen', res);

      if (res.ok) {
        const { data: responseData } = await res.json();
        console.log('ConfirmDonationScreen', responseData);
        handleUpdateFundraiserDonation(data.amount);
        setTransactionState('confirmed');
        setShowSuccess('user-external-donation');
      } else {
        // toast('Error confirming donation', { type: 'error' });
      }
    }
  };

  const handleCancelDonation = async () => {
    const formData = new FormData();

    const payload = {
      intentId: donationIntent?._id,
    };

    formData.append('data', JSON.stringify(payload));

    if (isLoggedIn()) {
      const { ok } = await authorizedServerFetch(
        `users/me/external/donations/confirm`,
        {
          method: 'PUT',
          body: formData,
        },
      );

      if (!ok) {
        console.log('Error in cancelling donation');
        return;
      }
    } else {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_HOST}/v1/public/donations/external/confirm`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: formData,
        },
      );

      console.log('handleCancelDonation', res);

      if (!res.ok) {
        const { data } = await res.json();
        console.log('Error in cancelling donation');
        return;
      }
    }

    setTransactionState('cancelled');
    handleClose();
  };

  useEffect(() => {
    setValue('amount', donationIntent?.amount);
  }, [donationIntent, setValue]);

  if (donationIntent?.status !== 'pledged') {
    const status =
      donationIntent?.status === 'confirmed' ? 'Confirmed' : 'Cancelled';
    return (
      <div className="flex flex-col">
        <div className="flex h-full flex-1 overflow-y-auto overflow-x-hidden">
          <div className="flex w-full items-center justify-between gap-4">
            <div className="mt-2 flex items-center justify-start truncate text-26 font-bold">
              Donation {status}
            </div>
          </div>
        </div>
        <form
          className="mt-2 flex flex-col justify-start gap-4"
          onSubmit={handleSubmit(handleFormSubmit)}>
          <div className="flex flex-col items-start gap-4">
            <Typography variant="subtitle1">
              {donationIntent?.status === 'confirmed'
                ? `Thank you! Your donation has already been confirmed. We appreciate your generosity and support.`
                : `Your donation has been cancelled. Please create another pledge and confirm it if you'd like to proceed.`}
            </Typography>
          </div>

          <div
            className={`flex w-full items-center justify-end gap-4 p-3 ${
              isLessThanSmallScreen ? 'mb-20 flex-col' : 'mb-4'
            }`}>
            <Button
              variant="outlined"
              color="primary"
              className={`${isLessThanSmallScreen ? 'w-full' : ''}`}
              onClick={handleClose}>
              Close
            </Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex h-full flex-1 overflow-y-auto overflow-x-hidden">
        <div className="flex w-full items-center justify-between gap-4">
          <div className="mt-2 flex items-center justify-start truncate text-26 font-bold">
            Confirm Donation
          </div>
        </div>
      </div>
      <form
        className="mt-2 flex flex-col justify-start gap-4"
        onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="flex flex-col items-start gap-4">
          <Typography variant="subtitle1">
            Have you successfully made your donation? If not,{' '}
            <a
              className="underline"
              href={nonProfit?.donation_url}
              target="_blank"
              rel="noopener noreferrer">
              click here{' '}
            </a>
            to donate
          </Typography>
          <div className="mt-4 grid w-full grid-cols-2 items-center gap-x-1 gap-y-3">
            <Typography variant="subtitle1" className="font-bold">
              Nonprofit name:
            </Typography>
            <Typography variant="body1" className="truncate">
              {nonProfit?.name}
            </Typography>

            <Typography variant="subtitle1" className="font-bold">
              Nonprofit address:
            </Typography>
            <Typography variant="body1" className="truncate">
              {addressString}
            </Typography>

            <Typography variant="subtitle1" className="font-bold">
              Nonprofit taxId:
            </Typography>
            <Typography variant="body1">{nonProfit?.ein}</Typography>

            <Typography variant="subtitle1" className="font-bold">
              Donation amount:
              {isAmountEditable && <span className="ml-1 text-red-700">*</span>}
            </Typography>
            <Typography variant="body1">
              {!isAmountEditable && (
                <div className="flex flex-row items-center gap-4">
                  <Typography>
                    {getSymbolFromCurrency(donationIntent?.currency)}{' '}
                    {savedAmount || donationIntent?.amount}
                  </Typography>
                  <Button
                    variant="outlined"
                    className="flex"
                    onClick={handleEditAmount}>
                    <MdEdit className="h-6 w-6" />
                  </Button>
                </div>
              )}
            </Typography>
            {isAmountEditable && (
              <div className="flex flex-row items-start gap-4">
                <Controller
                  name="amount"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <Input
                      placeholder="Enter an amount"
                      required
                      {...rest}
                      adornment={
                        <Typography
                          variant="body1"
                          color="textSecondary"
                          className="!font-medium">
                          {getSymbolFromCurrency(donationIntent?.currency)}
                        </Typography>
                      }
                      error={errors.amount?.message}
                    />
                  )}
                />
                <Button
                  variant="outlined"
                  className="flex items-center justify-center"
                  onClick={handleSaveAmount}>
                  <MdCheck className="h-6 w-6" />
                </Button>
                <Button
                  variant="filled"
                  className="flex items-center justify-center"
                  onClick={handleClearAmount}>
                  <MdClose className="white h-6 w-6" />
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="mt-2">
          <PostImageSelector
            value={donationImage}
            onChange={handleUpdateDonationImage}
            maxFiles={1}
            autoOpen={false}
            dropBoxMessage={
              'Upload your donation receipt or another proof of donation'
            }
          />
          <Typography
            variant="caption"
            color="error"
            className={!donationProofError && 'hidden'}>
            Donation proof is required
          </Typography>
        </div>

        <div
          className={`flex w-full items-center justify-end gap-4 p-3 ${
            isLessThanSmallScreen ? 'mb-20 flex-col' : 'mb-4'
          }`}>
          <Button
            variant="outlined"
            color="primary"
            className={`${isLessThanSmallScreen ? 'w-full' : ''}`}
            onClick={handleCancelDonation}>
            NO, Cancel the donation
          </Button>
          <Button
            variant="outlined"
            color="primary"
            className={`${isLessThanSmallScreen ? 'w-full' : ''}`}
            onClick={handleClose}>
            Remind me later
          </Button>
          <Button
            variant="filled"
            type="submit"
            className={`${isLessThanSmallScreen ? 'w-full' : ''}`}>
            Confirm
          </Button>
        </div>
      </form>
    </div>
  );
};

function ExternalDonationForm(props) {
  const {
    user,
    post,
    amount,
    nonProfit,
    isLoggedIn,
    handleClose,
    donationIntent,
    setShowSuccess,
    setLoggedOutUser,
    handleFetchDonationIntent,
  } = props;

  const [transactionState, setTransactionState] = useState(
    donationIntent ? 'confirm' : 'pending',
  );

  const renderExternalDonationScreen = () => {
    // const status = 'confirm';
    switch (transactionState) {
      case 'pending':
        return (
          <PendingDonationScreen
            user={user}
            post={post}
            amount={amount}
            nonProfit={nonProfit}
            isLoggedIn={isLoggedIn}
            setShowSuccess={setShowSuccess}
            setLoggedOutUser={setLoggedOutUser}
            setTransactionState={setTransactionState}
            handleFetchDonationIntent={handleFetchDonationIntent}
          />
        );
      case 'confirm':
        return (
          <ConfirmDonationScreen
            user={user}
            post={post}
            nonProfit={nonProfit}
            isLoggedIn={isLoggedIn}
            handleClose={handleClose}
            donationIntent={donationIntent}
            setShowSuccess={setShowSuccess}
            setTransactionState={setTransactionState}
          />
        );
      default:
        return '';
    }
  };

  return <>{renderExternalDonationScreen()}</>;
}

export default ExternalDonationForm;
